import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NZ_MODAL_DATA } from 'ng-zorro-antd/modal';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { finalize } from 'rxjs';
import { I18NService } from 'src/app/core';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

type RobotOrderStatus = 'Pending' | 'Paid' | 'Failed' | 'Canceled' | 'Expired' | 'Closed' | 'Replaced';

@Component({
  selector: 'robot-order-history-modal',
  standalone: true,
  imports: [CommonModule, I18nPipe, NzSpinModule, NzTagModule, NzIconModule, TestIdDirective],
  templateUrl: './robot-order-history-modal.component.html',
  styleUrls: ['./robot-order-history-modal.component.less']
})
export class RobotOrderHistoryModalComponent implements OnInit {
  private readonly http = inject(HttpService);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  readonly modalData: any = inject(NZ_MODAL_DATA, { optional: true }) || {};

  loading = false;
  robotId = '';
  robotName = '';
  pageIndex = 0;
  pageSize = 20;
  totalCount = 0;
  historyItems: any[] = [];
  loadingMore = false;
  hasNextPage = false;

  ngOnInit(): void {
    this.robotId = String(this.modalData?.robotId ?? '').trim();
    this.robotName = String(this.modalData?.name ?? '').trim();
    this.pageIndex = Number(this.modalData?.pageIndex ?? 0) || 0;
    this.pageSize = Number(this.modalData?.pageSize ?? 20) || 20;
    if (this.robotId) {
      this.loadHistory();
    }
  }

  loadHistory(pageIndex: number = this.pageIndex): void {
    if (!this.robotId) {
      this.historyItems = [];
      this.totalCount = 0;
      this.hasNextPage = false;
      return;
    }
    const nextPageIndex = Math.max(0, pageIndex);
    const append = nextPageIndex > 0;
    if (append) {
      if (this.loading || this.loadingMore || !this.hasNextPage) {
        return;
      }
      this.loadingMore = true;
    } else {
      this.loading = true;
    }
    this.pageIndex = nextPageIndex;
    this.http
      .post(ConstantUtil.ADMIN_ROBOT_ORDER_V1_LIST, {
        robotId: this.robotId,
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
        orderBy: this.modalData?.orderBy ?? 'createTime',
        direction: this.modalData?.direction ?? 'desc'
      })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.loadingMore = false;
        })
      )
      .subscribe({
        next: res => {
          const isSuccess = !!res?.success || res?.status === 0 || res?.status === 200;
          if (!isSuccess) {
            if (!append) {
              this.historyItems = [];
            }
            this.totalCount = 0;
            this.hasNextPage = false;
            return;
          }
          const data = res?.data ?? {};
          const incomingItems = Array.isArray(data?.items) ? data.items : [];
          this.historyItems = append ? [...this.historyItems, ...incomingItems] : incomingItems;
          this.totalCount = Number(data?.totalCount ?? this.historyItems.length ?? 0);
          this.hasNextPage = this.historyItems.length < this.totalCount;
        },
        error: () => {
          if (!append) {
            this.historyItems = [];
          }
          this.totalCount = 0;
          this.hasNextPage = false;
        }
      });
  }

  onListScroll(event: Event): void {
    const target = event.target as HTMLElement | null;
    if (!target || this.loading || this.loadingMore || !this.hasNextPage) {
      return;
    }
    const threshold = 80;
    const remaining = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (remaining <= threshold) {
      this.loadHistory(this.pageIndex + 1);
    }
  }

  getStatusLabel(status?: RobotOrderStatus | string | null): string {
    const normalized = String(status ?? '').trim();
    const keyMap: Record<string, string> = {
      Pending: 'robot-service.order.pending',
      Paid: 'robot-service.order.paid',
      Failed: 'robot-service.order.failed',
      Expired: 'payment.result.countdown.expired',
      Canceled: 'robot-service.order.canceled',
      Closed: 'robot-service.order.closed',
      Replaced: 'robot-service.order.replaced'
    };
    return this.i18n.fanyi(keyMap[normalized] ?? normalized);
  }

  getStatusColor(status?: RobotOrderStatus | string | null): 'blue' | 'green' | 'red' | 'orange' | 'default' {
    const normalized = String(status ?? '').trim();
    if (normalized === 'Pending') return 'orange';
    if (normalized === 'Paid') return 'green';
    if (['Failed', 'Expired'].includes(normalized)) return 'red';
    if (['Canceled', 'Closed', 'Replaced'].includes(normalized)) return 'default';
    return 'blue';
  }

  formatDate(value?: number | null): string {
    const ts = Number(value ?? 0);
    if (!Number.isFinite(ts) || ts <= 0) {
      return '-';
    }
    return new Intl.DateTimeFormat(this.i18n.currentLang || 'zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).format(new Date(ts));
  }

  formatNumber(value?: number | null): string {
    const num = Number(value ?? 0);
    if (!Number.isFinite(num) || num <= 0) {
      return '0';
    }
    return new Intl.NumberFormat(this.i18n.currentLang || 'zh-TW').format(num);
  }

  formatCurrency(value?: number | null): string {
    const num = Number(value ?? 0);
    if (!Number.isFinite(num)) {
      return new Intl.NumberFormat(this.i18n.currentLang || 'zh-TW', {
        style: 'currency',
        currency: 'CNY',
        maximumFractionDigits: 2
      }).format(0);
    }
    return new Intl.NumberFormat(this.i18n.currentLang || 'zh-TW', {
      style: 'currency',
      currency: 'CNY',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(num);
  }
}
